import { IconButton, type IconButtonProps } from "@ui-kit/Button";
import { DisabledReason, Tooltip, TooltipContent, TooltipShortcut, TooltipTrigger } from "@ui-kit/Tooltip";
import { forwardRef } from "react";

type Side = NonNullable<React.ComponentPropsWithoutRef<typeof TooltipContent>["side"]>;

export interface TooltipIconButtonProps extends IconButtonProps {
	tooltip: string;
	/**
	 * Shown instead of `tooltip` while the button is off. Naming what the button would do is no help
	 * when it cannot be pressed -- the user needs the reason, and needs it from the keyboard too.
	 */
	disabledReason?: string;
	tooltipSide?: Side;
	hotKey?: string;
}

export const TooltipIconButton = forwardRef<HTMLButtonElement, TooltipIconButtonProps>((props, ref) => {
	const { tooltip, disabledReason, tooltipSide, hotKey, "aria-label": ariaLabel = tooltip, ...rest } = props;
	const otherProps = { ...rest, "aria-label": ariaLabel };

	if (otherProps.disabled && disabledReason) {
		return (
			<DisabledReason reason={disabledReason}>
				<IconButton {...otherProps} ref={ref} />
			</DisabledReason>
		);
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<IconButton {...otherProps} ref={ref} />
			</TooltipTrigger>
			<TooltipContent side={tooltipSide}>
				{hotKey ? (
					<div className="flex items-center gap-2">
						{tooltip}
						<TooltipShortcut className="p-0" inverse value={hotKey} />
					</div>
				) : (
					tooltip
				)}
			</TooltipContent>
		</Tooltip>
	);
});
